import { css } from '@linaria/core'
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Search } from '#components/Search'
import { useShortcutContext } from '#features/Shortcuts/useShortcutContext'

const searchClass = css`
  position: sticky;
  top: 0;
`

type SidebarSearchProps<
  TFilterState extends {
    search?: string
  } = { search?: string },
> = {
  onFilterChange?: (filter: TFilterState) => void
  filterState: TFilterState
}

export function SidebarSearch<
  TFilterState extends {
    search?: string
  } = { search?: string },
>({ onFilterChange, filterState }: SidebarSearchProps<TFilterState>) {
  const lastFilterState = useRef<TFilterState>(filterState)
  const [fromAnimation, setFromAnimation] = useState(false)

  const ref = useRef<HTMLInputElement>(null)
  const {
    search: { setOnSearchInvoked },
  } = useShortcutContext()

  useEffect(() => {
    return setOnSearchInvoked({
      context: 'OptionTable Search',
      onSearch: (_, evt) => {
        if (ref.current) {
          evt.preventDefault()
          ref.current.focus()
          setFromAnimation(true)
        }
      },
    })
  }, [setOnSearchInvoked])

  useEffect(() => {
    if (lastFilterState.current !== filterState) {
      lastFilterState.current = filterState
    }
  }, [filterState])

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const searchValue = event.target.value
      onFilterChange?.({
        ...lastFilterState.current,
        search: searchValue.length === 0 ? undefined : searchValue,
      })
    },
    [onFilterChange],
  )

  return (
    <Search
      ref={ref}
      className={searchClass}
      onChange={onChange}
      value={filterState.search ?? ''}
      onBlur={() => setFromAnimation(false)}
      onClear={(e) => {
        onFilterChange?.({ ...lastFilterState.current, search: undefined })
        setFromAnimation(false)
        ;(e.currentTarget as HTMLElement).blur()
      }}
      focusAnimation={fromAnimation}
    />
  )
}
